// Статуси рахунку monobank. Бек зберігає їх як є, без власного маппінгу —
// перекладати в людські підписи має UI.
export type PaymentStatus =
  | 'created'
  | 'processing'
  | 'hold'
  | 'success'
  | 'failure'
  | 'reversed'
  | 'expired';

// Стани, у яких ще є сенс перепитувати статус: оплата або щойно створена, або
// в процесі. `failure` теж не кінець — клієнт може оплатити ще раз тим самим
// рахунком, але тоді він піде на сторінку банку, а не лишиться чекати тут.
export const isPendingPaymentStatus = (status?: string): boolean =>
  status === 'created' || status === 'processing';

// Клієнтська мапа причин відмови — навмисно КОРОТША за адмінську.
// Менеджеру потрібен точний код банку, покупцю — те, що він може виправити
// сам. Усе інше («помилка налаштувань торгової точки», внутрішні збої
// monobank) зводиться до одного нейтрального тексту: це наша проблема, а не
// його, і сипати технічними формулюваннями в покупця немає сенсу.
const CLIENT_ERROR_LABELS: Record<string, string> = {
  '59': 'на картці недостатньо коштів',
  '52': 'номер картки вказано невірно',
  '51': 'термін дії картки минув',
  '1080': 'термін дії картки вказано невірно',
  '1419': 'термін дії картки вказано невірно',
  '80': 'неправильний CVV-код',
  '81': 'неправильний CVV-код',
  '1200': 'потрібен CVV-код',
  '1014': 'потрібно вказати повні реквізити картки',
  '50': 'по картці встановлено обмеження на витрати',
  '58': 'по картці встановлено обмеження на покупки',
  '60': 'перевищено ліміт операцій по картці',
  '61': 'перевищено інтернет-ліміт картки',
  '63': 'перевищено інтернет-ліміт картки',
  '83': 'перевищено кількість спроб оплати',
  '1064': 'оплата можлива лише картками Visa або Mastercard',
  '1056': 'оплата можлива лише карткою українського банку',
  '84': 'не пройдено перевірку 3-D Secure',
  '1034': 'не пройдено перевірку 3-D Secure',
  '1035': 'не пройдено перевірку 3-D Secure',
  '1045': 'не пройдено перевірку 3-D Secure',
  '1054': 'не пройдено перевірку 3-D Secure',
  '1421': 'не пройдено перевірку 3-D Secure',
  '1422': 'не вдалося пройти 3-D Secure',
  '1425': 'не вдалося пройти 3-D Secure',
  '1429': 'не пройдено перевірку 3-D Secure',
  '1458': 'оплату відхилено на етапі 3-D Secure',
  '8004': 'проблема з перевіркою 3-D Secure',
  '6': 'операцію відхилив банк, що випустив картку',
  '55': 'операцію відхилив банк, що випустив картку',
  '71': 'операцію відхилив банк, що випустив картку',
  '72': 'операцію відхилив банк, що випустив картку',
  '75': 'операцію відхилив банк, що випустив картку',
  '1165': 'операцію відхилив банк, що випустив картку',
  '1193': 'операцію відхилив банк, що випустив картку',
  '1408': 'операцію відхилив банк, що випустив картку',
  '1413': 'операцію відхилив банк, що випустив картку',
  '1428': 'операцію відхилив банк, що випустив картку',
  '40': 'картку заблоковано',
  '41': 'картку заблоковано',
  '1405': 'оплату обмежено платіжною системою',
  '1406': 'оплату відхилено з міркувань безпеки',
  '1407': 'оплату відхилено з міркувань безпеки',
  '8001': 'минув термін дії посилання на оплату',
  '8002': 'оплату скасовано',
};

const DEFAULT_ERROR_LABEL = 'банк відхилив оплату';

// Бек кладе в failureReason склейку "errCode: failureReason". Беремо код на
// початку і шукаємо свій підпис; якщо коду немає в мапі — показуємо
// нейтральний текст, а НЕ сирий текст банку: там трапляються формулювання
// на кшталт «Здається, наш кіт облажався», які покупцю нічого не пояснюють.
export const getPaymentFailureLabel = (
  failureReason?: string | null
): string => {
  if (!failureReason) {
    return DEFAULT_ERROR_LABEL;
  }

  const separatorIndex = failureReason.indexOf(':');
  const code =
    separatorIndex === -1
      ? failureReason.trim()
      : failureReason.slice(0, separatorIndex).trim();

  if (/^\d+$/.test(code)) {
    return CLIENT_ERROR_LABELS[code] ?? DEFAULT_ERROR_LABEL;
  }

  return DEFAULT_ERROR_LABEL;
};
